import { Button } from '@mui/material';
import { ChevronRight, ChevronDown, Trash2, GitBranch } from 'lucide-react';
import type { ContentBlock } from './interface';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

interface ContentTreeNodeProps {
  block: ContentBlock;
  allBlocks: ContentBlock[];
  level: number;
  isSelected: boolean;
  isExpanded: boolean;
  expandedBlocks: Set<number>;
  selectedBlockId?: number;
  onSelectBlock: (block: ContentBlock) => void;
  onToggleExpanded: (blockId: number) => void;
  onDeleteBlock: (blockId: number) => void;
  parentPadding?: number;
}

const levelPadding: Record<number, number> = {
  0: 0,
  1: 20,
  2: 24,
  3: 28,
  4: 32,
  5: 36,
  6: 40,
  7: 44,
  8: 48,
  9: 52,
  10: 54,
};

export function ContentTreeNode({
  block,
  allBlocks,
  level,
  isSelected,
  isExpanded,
  expandedBlocks,
  selectedBlockId,
  onSelectBlock,
  onToggleExpanded,
  onDeleteBlock,
  parentPadding = 0,
}: ContentTreeNodeProps) {
  const [currentBlock, setCurrentBlock] = useState(block);

  useEffect(() => {
    const updated = allBlocks.find((b) => b.blockId === block.blockId);
    if (updated) setCurrentBlock(updated);
  }, [allBlocks, block.blockId]);

  const children = allBlocks.filter((b) => b.parentId === block.blockId);
  const hasChildren = children.length > 0;
  const currentPadding = parentPadding + (levelPadding[level] ?? 16);

  const theme = useTheme();

  return (
    <div className="relative">
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '12px',
          padding: '12px',
          borderRadius: '0.5rem',
          cursor: 'pointer',
          transition: 'all 0.15s',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          marginLeft: currentPadding,
        }}
        onClick={() => onSelectBlock(currentBlock)}
      >
        {hasChildren ? (
          <Button
            variant="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded(currentBlock.blockId);
            }}
            sx={{
              minWidth: 0,
              width: 30,
              height: 30,
              padding: 0,
              lineHeight: 0,
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </Button>
        ) : (
          <div className="w-6 h-6" />
        )}

        <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex-shrink-0">
          <GitBranch className="h-4 w-4 text-white" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">
            {currentBlock.label || `Блок ${currentBlock.blockId}`}
          </div>
          <div className="text-xs text-gray-500">
            Кнопки: {currentBlock.ButtonsToBlocks?.length || 0}
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-100">
          {currentBlock.isEditable !== false && (
            <Button
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteBlock(currentBlock.blockId);
              }}
            >
              <Trash2 className="h-4 w-4 text-red-500 hover:text-red-600" />
            </Button>
          )}
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className="mt-1 space-y-1">
          {children
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((child) => (
              <ContentTreeNode
                key={child.blockId}
                block={child}
                allBlocks={allBlocks}
                level={level + 1}
                isSelected={selectedBlockId === child.blockId}
                isExpanded={expandedBlocks.has(child.blockId)}
                expandedBlocks={expandedBlocks}
                selectedBlockId={selectedBlockId}
                onSelectBlock={onSelectBlock}
                onToggleExpanded={onToggleExpanded}
                onDeleteBlock={onDeleteBlock}
                parentPadding={currentPadding}
              />
            ))}
        </div>
      )}
    </div>
  );
}
